import {
  SaveWorkspaceEventPayload,
  EventRoom,
  SavePlaceholderCalendarRequestPayload,
} from '@chilipiper/api-type-def/src/events'
import {
  PlaceholderCalendar,
  ScrollableTableRequestV2,
  ScrollableTableResponse,
  UsersAtAllResponse,
} from '@chilipiper/api-type-def'
import {
  AssigneeGroup,
  BookerCalendarEvent,
  BookerEvent,
  Event,
  GroupEvent,
  Queue,
  RoomAvailability,
  EventUserAvailability,
  SortedRooms,
  Template,
  SeriesStatus,
} from '@chilipiper/api-type-def'
import { BookingLink } from '@chilipiper/api-type-def/src/bookingLinks'
import { post, get, del } from './api'

export const eventsService = {
  list() {
    return get('events/list-all').json<Event[]>()
  },
  create(json: SaveWorkspaceEventPayload) {
    return post('events/event/create', { json }).json<Event>()
  },
  getEventsByWorkspaceId(workspaceId: string) {
    return get(`events/list/${workspaceId}`).json<Event[]>()
  },
  getEventByWorkspaceId(workspaceId: string): Promise<Event | undefined> {
    return get(`events/list/${workspaceId}`)
      .json<Event[]>()
      .then(events => events[0])
      .catch(() => undefined)
  },
  booked(eventId: string) {
    return get(`events/event/booked/${eventId}`).json<{ result: number }>()
  },
  byId(eventId: string) {
    return get(`events/event/${eventId}`).json<BookerEvent>()
  },
  teams(eventId: string) {
    return get(`events/event/groups/list/${eventId}`).json<AssigneeGroup[]>()
  },
  templates(eventId: string) {
    return get(`events/event/templates/${eventId}`).json<Template[]>()
  },
  bookingLinks(eventId: string) {
    return get(`events/${eventId}/bookinglinks`).json<BookingLink[]>()
  },
  activeQueues(eventId: string) {
    return get(`events/event/queues/${eventId}?active=true`).json<Queue[]>()
  },
  getUserAvailablity(start: number, end: number, eventId: string) {
    return get(`events/users-availability/${eventId}?start=${start}&end=${end}`).json<
      EventUserAvailability[]
    >()
  },
}

interface RoomSlotsParams {
  end: number
  eventId: string
  roomsIds: string[]
  start: number
  step: number
}

export const roomsService = {
  save(room: EventRoom) {
    return post('rooms/save', {
      body: JSON.stringify(room),
    }).json<{ sucess: boolean }>()
  },
  byId(id: string) {
    return get(`rooms/${id}`).json<EventRoom>()
  },
  byEventId(eventId: string) {
    return get(`events/event/rooms/${eventId}`).json<EventRoom[]>()
  },
  delete(roomId: string) {
    return del(`rooms/${roomId}`).json<{ sucess: boolean }>()
  },
  availability(start: number, end: number, eventId: string, exclude?: string) {
    return get(
      `rooms/availability?start=${start}&end=${end}&eventId=${eventId}${
        exclude ? `&exclude=${exclude}` : ''
      }`
    ).json<RoomAvailability[]>()
  },
  sortByQueue(queueId: string) {
    return get(`rooms/sort/${queueId}`).json<SortedRooms[]>()
  },
  slots({ eventId, roomsIds, start, end, step }: RoomSlotsParams) {
    return post('rooms/slots', {
      json: {
        eventId,
        roomsIds,
        start,
        end,
        step,
      },
    }).json<BookerCalendarEvent[]>()
  },
}

export const placeholderCalendarService = {
  listAll: (eventId: string) => get(`events/${eventId}/placeholders`).json<PlaceholderCalendar[]>(),
  save: (payload: SavePlaceholderCalendarRequestPayload) =>
    post('placeholders/save', { json: payload }).json<PlaceholderCalendar>(),
  list: (payload: ScrollableTableRequestV2 & { eventId: string; isActive?: boolean }) =>
    post('placeholders', { json: payload }).json<ScrollableTableResponse<PlaceholderCalendar>>(),

  delete: (id: string) => del(`placeholders/${id}`),
  get: (id: string) => get(`placeholders/${id}`).json<PlaceholderCalendar>(),
}

export const workspaceUsersService = {
  getUsers: (payload: Omit<ScrollableTableRequestV2, 'pageSize'> & { workspaceId: string }) =>
    post('team/users', { json: payload }).json<UsersAtAllResponse>(),
  getCalenderConnectionStatus: (id: string) =>
    get(`business/user/is-calendar-connected/${id}`).json<boolean>(),
}

export const groupEventService = {
  list(workspaceId: string) {
    return get(`meeting-series/list/${workspaceId}`).json<GroupEvent[]>()
  },
  save(groupEvent: GroupEvent) {
    return post('meeting-series/save', {
      json: groupEvent,
    }).json()
  },
  delete(id: string) {
    return del(`meeting-series/${id}`).json()
  },
  getBookingBaseLink() {
    return get('link/book-base').json<{ link: string }>()
  },
  getMeetingSeriesStatus: (id: string) => get(`meeting-series/status/${id}`).json<SeriesStatus>(),
}
